/**
 * The main feed's "Recommended" order (P25028-237): which sort a search sends, and
 * which searches the server may answer from its feed cache.
 *
 * This one file is used by the browser (SearchPage.duck.js), by server-side rendering
 * (through the same duck) and by the server's cache (server/api-util/feedCache.js), so
 * all three always agree on what counts as "the main feed" and on its cache key.
 *
 * It is written as CommonJS with no imports on purpose: the server runs plain Node and
 * requires it straight from src/, and the browser build accepts CommonJS too.
 *
 * ================ How the order works ================
 *
 * Every listing carries four random scores in its metadata, `feedScoreA` … `feedScoreD`
 * (one per "variant"). Each visitor is given one variant for their visit (the
 * `feedVariant` cookie), so they page through one stable order, while other visitors see
 * a different one. Scores are written by the server only — see server/api-util/feedScores.js.
 */

const FEED_VARIANTS = ['A', 'B', 'C', 'D'];
const FEED_VARIANT_COOKIE = 'feedVariant';

// The Recommended option's key in the sort menu. Like 'relevance', it is never sent to
// Sharetribe: choosing it removes `sort` from the URL.
const RECOMMENDED_SORT_KEY = 'recommended';

// Must match RESULT_PAGE_SIZE in SearchPage.duck.js.
const FEED_PAGE_SIZE = 24;
// Only the first pages are cached. Deeper pages are rare and would only fill memory.
const FEED_CACHED_PAGES = 10;

const isFeedVariant = value => FEED_VARIANTS.includes(value);

const feedScoreKey = variant => `feedScore${variant}`;
// `createdAt` second: listings with no score yet (before the first shuffle has scored them)
// all tie, and Sharetribe returns ties in no fixed order, so a visitor could see one twice
// across pages. With it they come newest first, as the feed did before.
const feedSortParam = variant => `meta_${feedScoreKey(variant)},createdAt`;

const FEED_SORT_PATTERN = /^meta_feedScore([A-D]),createdAt$/;

/**
 * The `sort` to send to Sharetribe, or null to send none.
 *
 * - No sort chosen (or Recommended): the feed order, unless the search has a keyword or a
 *   location. Then Sharetribe's own order (relevance or distance) is kept, as before.
 * - Relevance chosen: none, so Sharetribe sorts by relevance.
 * - Anything else (Newest, Oldest, prices): sent as it is.
 */
const resolveFeedSort = ({ sort, keywords, origin, variant, relevanceKey }) => {
  const noSortChosen = !sort || sort === RECOMMENDED_SORT_KEY;
  if (sort && sort === relevanceKey) {
    return null;
  }
  if (noSortChosen) {
    if (keywords || origin) {
      return null;
    }
    return feedSortParam(isFeedVariant(variant) ? variant : FEED_VARIANTS[0]);
  }
  return sort;
};

// Parameters a main-feed search may carry. Anything else is a filter (category, price,
// keyword, size…), and filtered results are never cached.
const FEED_PARAM_KEYS = ['page', 'perPage', 'include', 'minStock', 'stockMode', 'sort', 'limit.images'];
const isFeedParamKey = key =>
  FEED_PARAM_KEYS.includes(key) || key.startsWith('fields.') || key.startsWith('imageVariant.');

// JSON with keys in a fixed order, so the same parameters always give the same text.
const stableStringify = value => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
};

/**
 * The cache key for a main-feed search, or null when the search must not be cached.
 *
 * Cached: the Recommended order, no filters, pages 1–10. The key holds the variant, the
 * page and every other parameter, so a request that asks for different fields can never
 * be served (or poison) another request's page.
 */
const feedCacheKey = params => {
  if (!params || typeof params !== 'object') {
    return null;
  }
  const match = FEED_SORT_PATTERN.exec(params.sort || '');
  if (!match) {
    return null;
  }
  const keys = Object.keys(params).filter(key => params[key] !== undefined);
  if (!keys.every(isFeedParamKey)) {
    return null;
  }
  const page = Number(params.page || 1);
  if (!Number.isInteger(page) || page < 1 || page > FEED_CACHED_PAGES) {
    return null;
  }
  if (Number(params.perPage) !== FEED_PAGE_SIZE) {
    return null;
  }
  const rest = keys
    .filter(key => key !== 'page' && key !== 'sort')
    .reduce((picked, key) => ({ ...picked, [key]: params[key] }), {});
  return `${match[1]}:${page}:${stableStringify(rest)}`;
};

module.exports = {
  FEED_VARIANTS,
  FEED_VARIANT_COOKIE,
  RECOMMENDED_SORT_KEY,
  FEED_PAGE_SIZE,
  FEED_CACHED_PAGES,
  isFeedVariant,
  feedScoreKey,
  feedSortParam,
  resolveFeedSort,
  feedCacheKey,
};
